import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, List, useTheme } from '@mui/material';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PopupDynamicFieldView } from 'constants/formBuilder';
import {
  TAG_ASSIGNED_DATE_DYNAMIC_FIELD,
  TAG_NAME_DYNAMIC_FIELD,
  TAGS_CATEGORY_NAME,
} from 'constants/forms';
import useDebounce from 'hooks/useDebounce';
import { checkIsTagAssignedDate, checkIsTagName, getNextViewId } from 'utils/forms';

import { formsTestIds } from '../../../forms.testIds';
import { getStyles } from '../DynamicField.styles';

import Categories from './Categories';
import { SCROLL_DELAY_MS } from './DynamicFieldItemsList.constants';
import { getFilteredDynamicFieldsSchema } from './DynamicFieldItemsList.utils';

const defaultArray = [];

const DynamicFieldItemsList = ({
  dynamicFieldConfig: { dynamicFieldsSchema },
  setParentView = _.noop,
  setChosenOption,
  chosenOption: [chosenOptionTitle, chosenOptionValue] = defaultArray,
  onSave = _.noop,
  onSelectField = null,
  addedValues = defaultArray,
}) => {
  const chosenElementRef = useRef();
  const theme = useTheme();
  const classes = getStyles(theme);

  const [search, setSearch] = useState('');
  const [textValue, setTextValue] = useState('');

  const filteredDynamicFieldsSchema = useMemo(
    () => getFilteredDynamicFieldsSchema(dynamicFieldsSchema, search),
    [dynamicFieldsSchema, search],
  );

  const isTagName = checkIsTagName(chosenOptionTitle);
  const isTagAssignedDate = checkIsTagAssignedDate(chosenOptionTitle);
  const isTag = isTagName || isTagAssignedDate;

  const parentField = useMemo(() => {
    const allTags = dynamicFieldsSchema.find(({ title }) => title === TAGS_CATEGORY_NAME);
    const fieldName = isTagName ? TAG_NAME_DYNAMIC_FIELD : TAG_ASSIGNED_DATE_DYNAMIC_FIELD;

    return allTags?.fields.find(({ title }) => title === fieldName);
  }, [dynamicFieldsSchema, isTagName]);

  const selectedCategoryValue = isTag ? parentField?.value : chosenOptionValue;

  useEffect(() => {
    if (!chosenOptionValue) {
      return;
    }

    chosenElementRef.current?.scrollIntoView({ block: 'center' });
  }, [chosenOptionValue]);

  const debouncedSearchChange = useDebounce(setSearch, SCROLL_DELAY_MS);

  const onSearchChange = useCallback(
    e => {
      e.preventDefault();

      const { value } = e.currentTarget;

      setTextValue(value);
      debouncedSearchChange(value);
    },
    [debouncedSearchChange],
  );

  const onSelectDynamicField = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      const { title, value, type, label, ignoreUserDefinedStyles } = e.currentTarget.dataset;

      if (onSelectField) {
        onSelectField({ value });

        return;
      }

      if (ignoreUserDefinedStyles === 'true') {
        onSave({ title, value, type, label, ignoreUserDefinedStyles });

        return;
      }

      const nextViewId = getNextViewId({ type });

      setChosenOption([title, value, type, label]);
      setParentView(nextViewId);

      if (nextViewId === PopupDynamicFieldView.FIELDS_LIST_VIEW) {
        onSave({ title, value, type, label });
      }
    },
    [onSave, onSelectField, setChosenOption, setParentView],
  );

  return (
    <Box sx={classes.wrapper}>
      <Box data-field="shadow" sx={classes.searchContainer}>
        <TextFieldCDS
          autoFocus
          className="w-full"
          dataTestId={formsTestIds.DYNAMIC_FIELD_ITEMS_LIST_SEARCH_INPUT}
          label="Dynamic Fields"
          name="dynamicFieldsSearch"
          placeholder="Search Dynamic Fields"
          value={textValue}
          variant="outlined"
          onChange={onSearchChange}
        />
      </Box>

      <List disablePadding sx={classes.list}>
        <Categories
          addedValues={addedValues}
          categories={filteredDynamicFieldsSchema}
          chosenElementRef={chosenElementRef}
          chosenOptionValue={selectedCategoryValue}
          hasSearch={!!search?.length}
          onSelectDynamicField={onSelectDynamicField}
        />
      </List>
    </Box>
  );
};

DynamicFieldItemsList.propTypes = {
  dynamicFieldConfig: PropTypes.object.isRequired,
  setChosenOption: PropTypes.func.isRequired,
  chosenOption: PropTypes.array,
  addedValues: PropTypes.arrayOf(PropTypes.string),
  setParentView: PropTypes.func,
  onSave: PropTypes.func,
  onSelectField: PropTypes.func,
};

export default DynamicFieldItemsList;
